import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { licenciasService } from '../services/licencias';
import toast from 'react-hot-toast';
import {
    ArrowLeft,
    List,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    HelpCircle,
    Building2,
} from 'lucide-react';

const ESTADO_CONFIG = {
    vigente:    { label: 'Vigente',        icon: CheckCircle2,  rowCls: 'bg-green-50 border-green-200', badgeCls: 'bg-green-100 text-green-700' },
    por_vencer: { label: 'Vence este mes', icon: AlertTriangle, rowCls: 'bg-blue-50 border-blue-200',   badgeCls: 'bg-blue-100 text-blue-700' },
    vencido:    { label: 'Vencido',        icon: XCircle,       rowCls: 'bg-red-50 border-red-200',     badgeCls: 'bg-red-100 text-red-700' },
    pendiente:  { label: 'Sin documento',  icon: HelpCircle,    rowCls: 'bg-gray-50 border-gray-200',   badgeCls: 'bg-gray-200 text-gray-600' },
};

const ORDEN_ESTADO = { vencido: 0, por_vencer: 1, pendiente: 2, vigente: 3 };

const ResumenLicenciasPage = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [filtro, setFiltro] = useState('');

    const cargar = async () => {
        setCargando(true);
        try {
            setItems(await licenciasService.resumen());
        } catch {
            toast.error('Error al cargar el resumen');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const itemsOrdenados = useMemo(() => {
        let lista = [...items];
        if (filtro) lista = lista.filter((it) => it.estado === filtro);
        return lista.sort((a, b) => ORDEN_ESTADO[a.estado] - ORDEN_ESTADO[b.estado] || a.almacen.localeCompare(b.almacen));
    }, [items, filtro]);

    const conteos = useMemo(() => {
        const c = { vencido: 0, por_vencer: 0, vigente: 0, pendiente: 0 };
        items.forEach((it) => { c[it.estado] = (c[it.estado] || 0) + 1; });
        return c;
    }, [items]);

    return (
        <Layout>
            <div className="p-6">
                <div className="mb-6 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/gestion-documental/licencias-itse')}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Volver"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <List className="h-7 w-7 text-teal-600" />
                            Licencias e ITSE — Todos los almacenes
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Estado de vencimiento de licencias y certificados por almacén.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={key}
                                onClick={() => setFiltro(filtro === key ? '' : key)}
                                className={`rounded-xl border p-4 text-center transition-all ${cfg.rowCls} ${filtro === key ? 'ring-2 ring-teal-500' : ''}`}
                            >
                                <Icon className="h-5 w-5 mx-auto mb-1" />
                                <p className="text-xl font-bold text-gray-800">{conteos[key] || 0}</p>
                                <p className="text-xs text-gray-600">{cfg.label}</p>
                            </button>
                        );
                    })}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4">
                        {filtro ? `Filtrado: ${ESTADO_CONFIG[filtro].label}` : 'Todos'} — {itemsOrdenados.length} documento{itemsOrdenados.length !== 1 ? 's' : ''}
                    </h2>

                    {cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                        </div>
                    )}

                    {!cargando && itemsOrdenados.length === 0 && (
                        <div className="text-center py-14 text-gray-400">
                            <Building2 className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">No hay documentos para mostrar.</p>
                        </div>
                    )}

                    {!cargando && itemsOrdenados.map((item) => {
                        const cfg = ESTADO_CONFIG[item.estado] || ESTADO_CONFIG.pendiente;
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={`${item.almacen}-${item.tipo_documento}`}
                                onClick={() => navigate(`/gestion-documental/licencias-itse?almacen=${encodeURIComponent(item.almacen)}`)}
                                className={`w-full flex items-center justify-between gap-3 border rounded-lg px-4 py-3 mb-2 text-left hover:shadow-sm transition-all ${cfg.rowCls}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <Icon className="h-5 w-5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-800 truncate">{item.almacen}</p>
                                        <p className="text-xs text-gray-500 truncate">{item.tipo_documento_nombre}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {item.documento && item.documento.fecha_renovacion && (
                                        <span className="text-xs text-gray-500">
                                            Vence: {new Date(item.documento.fecha_renovacion + 'T00:00:00').toLocaleDateString('es-PE')}
                                        </span>
                                    )}
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.badgeCls}`}>
                                        {cfg.label}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
};

export default ResumenLicenciasPage;
